import React from "react";

/**
 * Layout-matched placeholders for route chunks and independent dashboards.
 * They are deliberately visual-only: they do not simulate completed data.
 * Motion-sensitive users see a stable placeholder.
 */
const pulse = "rounded-xl bg-slate-200/80 motion-safe:animate-pulse";

function Line({ width = "w-full", height = "h-4" }) {
  return <div aria-hidden="true" className={`${pulse} ${width} ${height}`} />;
}

function Panel({ rows = 2 }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
      <Line width="w-2/5" height="h-5" />
      <div className="mt-5 space-y-3">
        {Array.from({ length: rows }, (_, i) => (
          <Line key={i} width={i === 1 ? "w-4/5" : "w-full"} />
        ))}
      </div>
    </div>
  );
}

export default function WorkspaceSkeleton({ variant = "route", label = "Loading workspace" }) {
  if (variant === "tile") {
    return (
      <div role="status" aria-label={label}
        className="min-h-[260px] rounded-2xl border border-slate-200 bg-white p-5">
        <span className="sr-only">{label}</span>
        <Line width="w-24" height="h-5" />
        <div className="mt-5"><Line width="w-1/2" height="h-7" /></div>
        <div className="mt-4 space-y-3"><Line/><Line width="w-3/4"/></div>
        <div className="mt-8"><Line height="h-11"/></div>
      </div>
    );
  }
  if (variant === "list") {
    return (
      <div role="status" aria-label={label} className="mt-5">
        <span className="sr-only">{label}</span>
        <Panel rows={2}/>
      </div>
    );
  }
  return (
    <div role="status" aria-label={label} className="min-h-[70vh] bg-slate-50 p-4 sm:p-6">
      <span className="sr-only">{label}</span>
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex items-center justify-between rounded-2xl bg-white p-4 shadow-sm">
          <Line width="w-28" height="h-7"/>
          <Line width="w-20" height="h-9"/>
        </div>
        <div className="mb-5"><Line width="w-48" height="h-7" /></div>
        <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {Array.from({ length: 4 }, (_, i) => <Panel key={i} rows={1}/>)}
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <Panel rows={3}/><Panel rows={3}/>
        </div>
      </div>
    </div>
  );
}
